import { Text } from "@solomon-labs/design/components/ui";
import { motion, useReducedMotion } from "motion/react";
import type { ReactElement, ReactNode } from "react";

import { cn } from "../../utils/cn";

/**
 * @scope console
 *
 * `ResultPanel` — terminal-state card for "your <thing> is done"
 * surfaces. Captures the icon-on-tone-bg + h3-as-h2 + small subtitle
 * + fine-print + CTA shape with a layered, staggered entry animation.
 *
 * Lives in `console/` because it bakes in the dense console
 * typography (`h3` title in an `<h2>`, `small` subtitle, `fine-print`
 * footnote). Two layouts:
 *
 *   - `page`  (default) — full-bleed centered overlay card. Use when
 *               the result panel IS the page (post-flow celebration,
 *               legacy/full-bleed callers).
 *   - `panel` — fits inside a sibling slot (e.g. the right pane of a
 *               two-column hub). Raised surface, no border, stretched.
 *
 * Consumers compose the panel with their own icon, copy, and CTA —
 * the panel orchestrates the entry animation + layout. The wrapper
 * concerns (auto-redirect timer, scenario-driven copy, page chrome)
 * stay outside.
 *
 * Currently `tone="success"` is the only supported tone. Extend to
 * `"error"` / `"warning"` when a second consumer needs them — keep
 * the tone enum tight per ARCHITECTURE.md principle #6.
 */
export interface ResultPanelProps {
    /**
     * Tone — drives the icon background color. `success` →
     * `bg-success-inverse`. Start with one tone; extend later if a
     * second consumer needs a different palette.
     */
    tone?: "success";
    /**
     * Icon node — caller supplies the icon at the size + color they
     * want. For success-tone we expect a green-on-success-inverse
     * check-style glyph at ~h-10 w-10.
     */
    icon: ReactNode;
    /**
     * Required title. Renders as `<Text variant="h3" render={<h2/>}>`
     * (h3 typography in an `<h2>` element) so the page-h1 stays at the
     * top of the document and the result card reads as a sub-section
     * heading rather than competing.
     */
    title: ReactNode;
    /** Optional subtitle (small variant). */
    subtitle?: ReactNode;
    /** Vertical spacing between the title and subtitle. */
    subtitleSpacing?: "default" | "relaxed";
    /** Optional fine-print line above the CTA. */
    footnote?: ReactNode;
    /** Optional primary CTA. Caller passes a fully-built button. */
    cta?: ReactNode;
    /**
     * Layout variant. `page` is full-bleed centered; `panel` fits
     * inside a sibling slot. See JSDoc above for the visual split.
     * Default `"page"`.
     */
    layout?: "page" | "panel";
    /**
     * Disable the entry animations. Honored for `prefers-reduced-
     * motion` automatically; pass `true` to force-disable for testing
     * or for cases where the consumer is animating the wrapper itself.
     */
    skipAnimation?: boolean;
    /**
     * Pacing multiplier — durations + delays scale by this factor.
     * Use `2` for the panel layout when running concurrently with
     * another stagger (e.g. the row-checkmark sequence in onboarding
     * approval) so the two sequences feel layered rather than racing.
     * Default `1`.
     */
    pacingScale?: number;
}

const ANIMATION_EASING: [number, number, number, number] = [0.4, 0, 0.2, 1];

export function ResultPanel({
    tone: _tone = "success",
    icon,
    title,
    subtitle,
    subtitleSpacing = "default",
    footnote,
    cta,
    layout = "page",
    skipAnimation = false,
    pacingScale = 1
}: ResultPanelProps): ReactElement {
    const reduced = useReducedMotion();
    const animate = !skipAnimation && !reduced;
    const slow = pacingScale;

    // Panel layout: structural-only wrapper. Top breathing room is the
    // consumer's responsibility (the panel often renders adjacent to a
    // title row above it; doubling the spacing creates visible gap drift).
    // Page layout: full-bleed centered with its own vertical padding.
    const sectionClass =
        layout === "panel" ? "block w-full" : "flex justify-center";

    // Page layout: chromeless — content centered on the page with a fixed max
    //   width, no surface or border (sits directly on the page background).
    // Panel layout: raised card, no border, stretched to the
    //   container's width (the parent slot already provides the outer
    //   horizontal padding so the card hugs the content edge).
    const cardClass =
        layout === "panel"
            ? "bg-surface-raised rounded-xs px-10 py-12 w-full text-center"
            : "px-12 py-12 max-w-xl w-full text-center";

    return (
        <section className={sectionClass}>
            <motion.div
                initial={
                    animate ? { opacity: 0, y: 8, scale: 0.98 } : { opacity: 0 }
                }
                animate={
                    animate ? { opacity: 1, y: 0, scale: 1 } : { opacity: 1 }
                }
                transition={{
                    duration: animate ? 0.6 * slow : 0.2,
                    ease: ANIMATION_EASING
                }}
                className={cn(cardClass, layout === "panel" && "mt-6")}
            >
                <motion.div
                    initial={
                        animate ? { opacity: 0, scale: 0.7 } : { opacity: 0 }
                    }
                    animate={
                        animate ? { opacity: 1, scale: 1 } : { opacity: 1 }
                    }
                    transition={{
                        duration: animate ? 0.45 * slow : 0.2,
                        ease: ANIMATION_EASING
                    }}
                    className="flex justify-center pb-6"
                >
                    <span className="bg-success-inverse rounded-full p-4 inline-flex">
                        {icon}
                    </span>
                </motion.div>
                <motion.div
                    initial={animate ? { opacity: 0, y: 6 } : { opacity: 0 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                        delay: animate ? 0.4 * slow : 0,
                        duration: animate ? 0.4 * slow : 0.2,
                        ease: ANIMATION_EASING
                    }}
                >
                    <Text
                        variant="h1"
                        render={<h1 />}
                        className="block leading-tight text-foreground-base"
                    >
                        {title}
                    </Text>
                </motion.div>
                {subtitle !== undefined && subtitle !== null && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{
                            delay: animate ? 0.6 * slow : 0,
                            duration: animate ? 0.3 * slow : 0.2
                        }}
                    >
                        <Text
                            variant="small"
                            className={cn(
                                "block leading-none text-foreground-muted",
                                subtitleSpacing === "relaxed" ? "pt-3" : "pt-1"
                            )}
                        >
                            {subtitle}
                        </Text>
                    </motion.div>
                )}
                {(footnote !== undefined && footnote !== null) ||
                cta !== undefined ? (
                    <motion.div
                        initial={
                            animate ? { opacity: 0, y: 4 } : { opacity: 0 }
                        }
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            delay: animate ? 0.8 * slow : 0,
                            duration: animate ? 0.3 * slow : 0.2
                        }}
                        className="pt-12"
                    >
                        {footnote !== undefined && footnote !== null && (
                            <Text
                                variant="body"
                                className="block leading-snug text-foreground-base pb-4"
                            >
                                {footnote}
                            </Text>
                        )}
                        {cta}
                    </motion.div>
                ) : null}
            </motion.div>
        </section>
    );
}
